export function useEventListeners(context) {
	const listeners = new Map();

	function addListener(target, event, handler) {
		const boundHandler = handler.bind(context);
		listeners.set(handler, { target, event, boundHandler });
		target.addEventListener(event, boundHandler);
	}

	function removeListener(target, event, handler) {
		const entry = listeners.get(handler);
		if (entry) {
			target.removeEventListener(event, entry.boundHandler);
			listeners.delete(handler);
		}
	}

	function removeAllListeners() {
		for (const { target, event, boundHandler } of listeners.values()) {
			target.removeEventListener(event, boundHandler);
		}
		listeners.clear();
	}

	return {
		addListener,
		removeListener,
		removeAllListeners,
	};
}

export function getElementFromEvent(e, selector) {
	if (!e.target) {
		return null;
	}

	const el = e.target?.closest(selector);

	if (!el) {
		return null;
	}

	return el;
}

export function getKeyboardFocusableElements(element = document) {
	return [
		...element.querySelectorAll('a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'),
	].filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
}

export function trapFocus(element, focusableElements) {
	if (focusableElements.length === 0) {
		return null;
	}

	let currentIndex = 0;

	focusableElements[currentIndex].focus();

	function handleTab(e) {
		const isTabPressed = e.key === 'Tab';

		if (!isTabPressed) {
			return;
		}

		let newIndex;

		if (e.shiftKey) {
			newIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
		} else {
			newIndex = (currentIndex + 1) % focusableElements.length;
		}

		focusableElements[newIndex].focus();
		currentIndex = newIndex;

		e.preventDefault();
	}

	element.addEventListener('keydown', handleTab);

	return {
		remove: function removeTrapFocus() {
			element.removeEventListener('keydown', handleTab);
		},
		focus: function focusTrapFocus() {
			focusableElements[currentIndex].focus();
		},
	};
}

export function track(event, data = {}) {
	if (!window.plausible) {
		return;
	}

	window.plausible(event, data);

	/*
		Events:
	
		Click event pop-up
		Open event
		Click collaborate nav button
		Switch language
		Play video
		Click cta button

		Properties:

		title
	*/
}

export function selectMatchingVideoSource(video) {
	return new Promise((resolve) => {
		if (!video) {
			return;
		}
		const rect = video.getBoundingClientRect();

		const clientWidth = rect.width * window.devicePixelRatio;
		const clientHeight = rect.height * window.devicePixelRatio;
		const clientRatio = clientWidth / clientHeight;

		const videoWidth = video.getAttribute('width');
		const videoHeight = video.getAttribute('height');
		const videoRatio = videoWidth / videoHeight;

		// from 0 to 1 (0.5 - 50%, 1 = 100%)
		const quality = video.getAttribute('data-quality') ?? 1;

		let width = '';
		if (clientRatio > videoRatio) {
			width = clientWidth * quality;
		} else {
			width = clientHeight * videoRatio * quality;
		}

		let matchedSource = null;
		let prevDiff = 999999;

		// Loop over all video sources
		const sources = video.querySelectorAll('source');
		const totalSourceCount = sources.length;
		let checkedSourcesCount = 0;

		const checkSources = new Promise((resolve, reject) => {
			Array.from(sources).forEach((source) => {
				// Get source resolution (provided by data attribute)
				let sourceWidth = source.dataset.sourcewidth;

				// Compare source resolution to video element width
				let currentDiff = sourceWidth - width;
				if (currentDiff <= 0) {
					currentDiff = currentDiff * -1;
				}
				if (currentDiff <= prevDiff) {
					matchedSource = source;
				}
				prevDiff = currentDiff;

				setTimeout(() => {
					checkedSourcesCount++;
					if (checkedSourcesCount >= totalSourceCount) {
						resolve('Checked All Sources');
					}
				}, 0);
			});
		});

		checkSources.then(() => {
			if (!matchedSource) {
				return;
			}
			if (video.src != matchedSource.src) {
				video.pause();
				video.src = matchedSource.src;
				resolve();
			}
		});
	});
}
